import React, {ChangeEvent, ReactNode, useRef, useState} from 'react'
import styled from '@emotion/styled'
import Text from "../text/Text";
import Colors from "../../styles/Colors";

interface Props {
    maxLength?: number,
    minLength?: number,
    placeholder: string,
    isImportant?: boolean,
    error?: string,
    onTextChange: Function,
    value: string,
    adornmentLeft?: ReactNode | Function,
    adornmentRight?: ReactNode | Function,
    rows?: number,
}

const TextFieldInput = (
    {
        maxLength = 2000,
        minLength = 0,
        placeholder = "",
        error = "",
        onTextChange = () => null,
        isImportant = false,
        value = "",
        adornmentLeft,
        adornmentRight,
        rows: initRows = 2,
    }: Props
) => {
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const labelContainerRef = useRef<HTMLDivElement>(null)
    const labelTextRef = useRef<HTMLElement>(null)

    const [rows, setRows] = useState(initRows)

    const handleTextChange = ({currentTarget}: ChangeEvent<HTMLTextAreaElement>) => {
        const lineHeight = 20
        const prevRows = currentTarget.rows

        // reset rows to init value
        currentTarget.rows = initRows

        // calc new rows amount
        const nextRows = Math.ceil((currentTarget.scrollHeight - 10) / lineHeight)

        // update rows amount if needed
        if (prevRows === nextRows) {
            currentTarget.rows = nextRows
        } else if (prevRows !== nextRows) {
            currentTarget.rows = nextRows
            setRows(nextRows)
        }

        if (currentTarget.value.length <= maxLength) {
            onTextChange(currentTarget.value)
        }
    }

    const handleFocus = () => {
        const labelText = labelTextRef.current
        const labelContainer = labelContainerRef.current

        if (labelText && labelContainer) {
            if (!value) {
                labelText.classList.toggle("is-focus")
                labelContainer.classList.toggle("is-focus")
            }
        }
    }

    const handleLabelClick = () => {
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    return (
        <Wrapper>
            <Container>
                {adornmentLeft}

                <InputContainer>
                    <LabelContainer
                        onClick={handleLabelClick}
                        ref={labelContainerRef}
                    >
                        <Text getRef={labelTextRef}>{placeholder}</Text>
                        {isImportant && <Text color={Colors.green}> *</Text>}
                    </LabelContainer>

                    <Input
                        ref={inputRef}
                        onChange={handleTextChange}
                        onFocus={handleFocus}
                        onBlur={handleFocus}
                        maxLength={maxLength}
                        minLength={minLength}
                        value={value}
                        rows={rows}
                    />

                    <RemainingTextContainer>
                        <Text
                            color={
                                (maxLength - value.length) < 100
                                    ? (maxLength <= value.length) ? Colors.error : Colors.warning
                                    : Colors.darkGrey
                            }
                            size={"12px"}
                        >
                            {`${maxLength - value.length}`}
                        </Text>

                        <Text size={"12px"}>: caractères restants</Text>
                    </RemainingTextContainer>
                </InputContainer>

                {adornmentRight}
            </Container>

            {
                error && <Text
                    color={Colors.red}
                    size={"11px"}
                >
                    {error}
                </Text>
            }
        </Wrapper>
    )
}

export default TextFieldInput

const Wrapper = styled.div`
  width: 40%;
  
  @media (max-width: 768px) {
      width: 100%;
  }
  
  p {
    text-align: start;  
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const LabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  width: 100%;
  
  p {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
  
  &:hover {
    cursor: text;
  }
`

const InputContainer = styled.div`
  width: 100%;
  position: relative;
  padding-top: 20px;
  
  div {
    animation: ease;
    transition-duration: 200ms;
    position: absolute;
    top: 20px;
    left: 5px;
  }
  
  div.is-focus {
    top: 3px;
    left: 0;
  }
  
  div > p {
    text-align: start;
    animation: ease;
    transition-duration: 200ms;
    opacity: 80%;
  }

  div > p.is-focus { 
    font-size: 12px;
    opacity: 100%;
  }
`

const Input = styled.textarea`
  width: 100%;
  border-radius: 2px;
  border: 1px solid ${Colors.darkGrey + "99"};
  padding: 5px;
  caret-color: ${Colors.green};
  line-height: 20px;
`

const RemainingTextContainer = styled.div`
  position: static !important;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  
  p:last-child {
    margin-left: 3px;
  }
`
